import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useState} from 'react';

import ProjectJsonPanel from '../components/project-json/project-json.jsx';
import downloadBlob from '../lib/download-blob';
import log from '../lib/log';
import {getProjectJson} from '../lib/project-json';
import {getProjectPython} from '../lib/project-python';

const ProjectJson = ({vm, onClose}) => {
    const [scope, setScope] = useState('target');
    const [language, setLanguage] = useState('json');
    const [snapshot, setSnapshot] = useState({text: '', name: ''});
    const [failed, setFailed] = useState(false);
    const [copyStatus, setCopyStatus] = useState('');
    const refresh = useCallback(() => {
        try {
            const nextSnapshot = language === 'python' ? getProjectPython(vm, scope) : getProjectJson(vm, scope);
            setSnapshot(previous => (
                previous.text === nextSnapshot.text && previous.name === nextSnapshot.name ? previous : nextSnapshot
            ));
            setFailed(false);
        } catch (error) {
            log.warn(`ProjectJson: serialization failed (scope=${scope}): ${error.message}`);
            setSnapshot({text: '', name: ''});
            setFailed(true);
        }
    }, [vm, scope, language]);

    useEffect(() => {
        refresh();
        let timer = null;
        const scheduleRefresh = () => {
            // Throttle rather than debounce: running projects may emit updates every frame.
            if (timer !== null) return;
            timer = setTimeout(() => {
                timer = null;
                refresh();
            }, 250);
        };
        const events = ['PROJECT_CHANGED', 'PROJECT_LOADED', 'targetsUpdate', 'workspaceUpdate'];
        events.forEach(event => vm.addListener(event, scheduleRefresh));
        return () => {
            clearTimeout(timer);
            events.forEach(event => vm.removeListener(event, scheduleRefresh));
        };
    }, [vm, refresh]);

    useEffect(() => setCopyStatus(''), [snapshot.text]);

    const copy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(snapshot.text);
            setCopyStatus('copied');
        } catch (error) {
            setCopyStatus('copyFailed');
        }
    }, [snapshot.text]);
    const download = useCallback(() => {
        const extension = language === 'python' ? 'py' : 'json';
        downloadBlob(`${scope === 'project' ? 'project' : 'target'}.${extension}`,
            new Blob([snapshot.text], {type: language === 'python' ?
                'text/x-python;charset=utf-8' : 'application/json;charset=utf-8'}));
    }, [scope, snapshot.text, language]);

    return (
        <ProjectJsonPanel
            copyStatus={copyStatus}
            failed={failed}
            language={language}
            name={snapshot.name}
            scope={scope}
            text={snapshot.text}
            warnings={snapshot.warnings || 0}
            onClose={onClose}
            onCopy={copy}
            onDownload={download}
            onLanguageChange={setLanguage}
            onRefresh={refresh}
            onScopeChange={setScope}
        />
    );
};

ProjectJson.propTypes = {
    onClose: PropTypes.func.isRequired,
    vm: PropTypes.shape({
        addListener: PropTypes.func.isRequired,
        editingTarget: PropTypes.shape({id: PropTypes.string}),
        removeListener: PropTypes.func.isRequired,
        toJSON: PropTypes.func.isRequired
    }).isRequired
};

export default ProjectJson;
